import { logger } from '../utils/index.js';
import { type ClientErrorCode } from './constants.js';

const ITS_FACILITY = {
  0: 'NOT_USED',
  1: 'NOT_USED_2',
  2: 'AGENT',
  3: 'GATEWAY',
  4: 'RNGW',
  5: 'TM',
  6: 'ASM',
  7: 'RET',
  8: 'GAME',
  9: 'SCORE',
  10: 'MCAST',
  11: 'CAMP',
  12: 'VAL',
  13: 'LOTT',
  14: 'SM',
  15: 'LM',
  16: 'TSM',
  17: 'DCACHE',
  18: 'WIN',
  19: 'TSTORE',
  20: 'MBVAL',
  21: 'OMS',
  22: 'SYN',
  23: 'BRC',
  24: 'SUB',
  25: 'PAY',
  26: 'VOM',
  27: 'COMP',
  28: 'PACC',
  29: 'TACC',
  30: 'RES',
  31: 'INVALID_Facility',
  99: 'NONE',
} as const;

type ITSFacilityNumeric = keyof typeof ITS_FACILITY;
export type ITSFacility = (typeof ITS_FACILITY)[ITSFacilityNumeric];

const ITS_NOT_USED = {} as const;
type ITS_NOT_USED_NUMERIC = keyof typeof ITS_NOT_USED;
type ITS_NOT_USED = (typeof ITS_NOT_USED)[ITS_NOT_USED_NUMERIC];

const ITS_NOT_USED2 = {
  '019': 'NOT_USED2_SUSPENSION',
} as const;
type ITS_NOT_USED2_NUMERIC = keyof typeof ITS_NOT_USED2;
type ITS_NOT_USED2 = (typeof ITS_NOT_USED2)[ITS_NOT_USED2_NUMERIC];

const ITS_AGENT = {} as const;
type ITS_AGENT_NUMERIC = keyof typeof ITS_AGENT;
type ITS_AGENT = (typeof ITS_AGENT)[ITS_AGENT_NUMERIC];

const ITS_GATEWAY = {
  '112': 'TAS_UNAVAILABLE',
} as const;
type ITS_GATEWAY_NUMERIC = keyof typeof ITS_GATEWAY;
type ITS_GATEWAY = (typeof ITS_GATEWAY)[ITS_GATEWAY_NUMERIC];

const ITS_RNGW = {} as const;
type ITS_RNGW_NUMERIC = keyof typeof ITS_RNGW;
type ITS_RNGW = (typeof ITS_RNGW)[ITS_RNGW_NUMERIC];

const ITS_TM = {
  '117': 'TM_GENERIC',
  '135': 'TM_GENERIC2',
} as const;
type ITS_TM_NUMERIC = keyof typeof ITS_TM;
type ITS_TM = (typeof ITS_TM)[ITS_TM_NUMERIC];

const ITS_ASM = {
  '101': 'ASM_INVALID_HANDLE' /* INVALID HANDLE */,
  '102': 'ASM_WRONG_ALIAS_LENGTH' /* WRONG ALIAS LENGTH */,
  '103': 'ASM_NUM_ERROR_BANKCARD' /* NUM ERROR BANKCARD */,
  '104': 'ASM_WRONG_PASSWORD' /* WRONG PASSWORD */,
  '105': 'ASM_USER_ALREADY_EXISTS' /* USER ALREADY EXISTS */,
  '106': 'ASM_EXPIRE_DATE_LENGTH' /* EXPIRE DATE LENGTH */,
  '107': 'ASM_CARD_NUMBER_LENGTH' /* CARD NUMBER LENGTH */,
  '108': 'ASM_INSERT_ACCHIST_FAILED' /* INSERT ACCHIST FAILED */,
  '109': 'ASM_UPDATE_BALANCE_FAILED' /* UPDATE BALANCE FAILED */,
  '110': 'ASM_NOPRIV' /* NO PRIVS */,
  '111': 'ASM_WRONG_SERIAL_NUM' /* WRONG SERIAL NUM */,
  '112': 'ASM_PASSWORD_LOCK' /* PASSWORD LOCK */,
  '113': 'ASM_NO_PRIV' /* NO PRIVILIGES */,
  '114': 'ASM_CHANGE_PASSWORD_REQUIRED' /* CHANGE PASSWORD REQUIRED */,
  '115': 'ASM_WRONG_CHECKSUM' /* WRONG CHECKSUM */,
  '116': 'ASM_USER_NOT_INTERNAL' /* USER NOT INTERNAL */,
  '117': 'ASM_USER_NOT_EXTERNAL' /* USER NOT EXTERNAL */,
  '118': 'ASM_OWNERSHIP_FAILURE' /* OWNERSHIP FAILURE */,
  '119': 'ASM_OTHER_USER_ACCESSED' /* OTHER_USER_ACCESSED */,
  '120': 'ASM_TARGET_SESSION_NOT_ACTIVE' /* TARGET SESSION NOT ACTIVE */,
  '121': 'ASM_NOT_ENOUGH_MONEY' /* NOT_ENOUGH_MONEY  */,
  '122': 'ASM_CUSTOMERNOTAUTHORIZED' /* CUSTOMER NOT AUTHORIZED */,
  '123': 'ASM_WRONG_USER_GROUP' /* WRONG USER GROUP */,
  '124': 'ASM_PASSWORD_NOT_CHANGED' /* PASSWORD NOT CHANGED */,
  '125': 'ASM_PASSWORD_LENGTH' /* PASSWORD LENGTH */,
  '126': 'ASM_PASSWORD_NO_DIGIT' /* PASSWORD NO DIGIT */,
  '127': 'ASM_PASSWORD_SAME_DIGITS' /* PASSWORD SAME DIGITS */,
  '128': 'ASM_PASSWORD_SEQUENCE' /* PASSWORD SEQUENCE */,
  '129': 'ASM_PASSWORD_PERSONAL_ID' /* PASSWORD PERSONAL ID */,
  '130': 'ASM_PASSWORD_USERID' /* PASSWORD USERID */,
  '131': 'ASM_NO_SUCH_PROPERTY' /* NO SUCH PROPERTY */,
  '132': 'ASM_PROPERTY_SET' /* PROPERTY SET */,
  '133': 'ASM_PROPERTY_CLEARED' /* PROPERTY CLEARED */,
  '134': 'ASM_CLOSE_TOO_LATE' /* CLOSE TOO LATE */,
  '135': 'ASM_OPEN_CLOSE_MISMATCH' /* OPEN CLOSE MISMATCH */,
  '136': 'ASM_ADJUST_CUSTOMER_ACCOUNT' /* ADJUST CUSTOMER ACCOUNT */,
  '137': 'ASM_MONTRANS_FEE_NOT_ALLOWED' /* MONTRANS FEE NOT ALLOWED */,
  '138': 'ASM_WRONG_MONTRANS_REF_NUM' /* WRONG MONTRANS REF NUM */,
  '139': 'ASM_WRONG_MONTRANS_REASON' /* WRONG MONTRANS REASON */,
  '140': 'ASM_WRONG_MONTRANS_TYPE' /* WRONG MONTRANS TYPE */,
  '141': 'ASM_WRONG_MONTRANS_REQ_TYPE' /* WRONG MONTRANS REQ TYPE */,
  '142': 'ASM_WRONG_MONTRANS_CUSTOMER' /* WRONG MONTRANS CUSTOMER */,
  '143': 'ASM_WRONG_MONTRANS_SEQ_NUM' /* WRONG MONTRANS SEQ NUM */,
  '144': 'ASM_WRONG_MONTRANS_AMOUNT' /* WRONG MONTRANS AMOUNT */,
  '145': 'ASM_WRONG_MONTRANS_FEE' /* WRONG MONTRANS FEE */,
  '146': 'ASM_BELOW_LIMIT' /* BELOW LIMIT */,
  '147': 'ASM_ABOVE_LIMIT' /* ABOVE LIMIT */,
  '148': 'ASM_FUNCTION_CLOSED' /* FUNCTION CLOSED */,
  '149': 'ASM_NO_ACCOUNT_USAGE' /* NO ACCOUNT USAGE */,
  '150': 'ASM_ABORTED_SEQ_NUM' /* ABORTED SEQ NUM */,
  '151': 'ASM_NO_FREE_IBANKIDX' /* NO FREE IBANKIDX */,
  '152': 'ASM_REF_NUM_NOT_ALLOWED' /* REF NUM NOT ALLOWED */,
  '153': 'ASM_REF_NUM_MISSING' /* REF NUM MISSING */,
  '154': 'ASM_DRAW_LIMIT_REACHED' /* DRAW LIMIT REACHED */,
  '155': 'ASM_INCORRECT_WAGER_BUDGET' /* INCORRECT WAGER BUDGET */,
  '156': 'ASM_PROPERTY_CHANGE' /* PROPERTY CHANGE */,
  '157': 'ASM_USER_GROUP_NOT_CHANGED' /* USER GROUP NOT CHANGED */,
  '158': 'ASM_TIMEOUT' /* ASM TIMEOUT */,
  '159': 'ASM_FUNCTION_CLOSED_DAYTIME' /* FUNCTION CLOSED DAYTIME */,
  '160': 'ASM_BANKCARD_NOT_ALLOWED' /* BANKCARD NOT ALLOWED */,
  '161': 'ASM_PENDING_LIMIT_REACHED' /* PENDING LIMIT REACHED */,
  '162': 'ASM_UNABLE_TO_PROCESS' /* UNABLE TO PROCESS */,
  '163': 'ASM_MERCHANT_NOT_ON_FILE' /* MERCHANT NOT ON FILE */,
  '164': 'ASM_INVALID_TRANSACTION' /* INVALID TRANSACTION */,
  '165': 'ASM_INVALID_CARD_NO' /* INVALID CARD NO */,
  '166': 'ASM_EXPIRED_CARD' /* EXPIRED CARD */,
  '167': 'ASM_RESTRICTED_CARD_PICK_UP' /* RESTRICTED CARD PICK UP */,
  '168': 'ASM_STOLEN_CARD_PICK_UP' /* STOLEN CARD PICK UP */,
  '169': 'ASM_NOT_SUFFICIENT_FUNDS' /* NOT SUFFICIENT FUNDS */,
  '170': 'ASM_EXCEEDS_AMOUNT_LIMIT' /* EXCEEDS AMOUNT LIMIT */,
  '171': 'ASM_DUPLICATE_TRANSACTION' /* DUPLICATE TRANSACTION */,
  '172': 'ASM_UNABLE_TO_AUTHORIZE' /* UNABLE TO AUTHORIZE */,
  '173': 'ASM_ABOVE_DAILY_LIMIT' /* ABOVE DAILY LIMIT */,
  '174': 'ASM_CARD_NOT_SUPPORTED' /* CARD NOT SUPPORTED */,
  '175': 'ASM_BANKACCOUNT_NOT_ALLOWED' /* BANKACCOUNT NOT ALLOWED */,
  '176': 'ASM_PENDING_BANKCARD' /* PENDING BANKCARD */,
  '177': 'ASM_NO_ACTIVE_ACCESS_PASSWORD' /* NO ACTIVE ACCESS PASSWORD */,
  '178': 'ASM_DENIALED_ACCESS_PASSWORD' /* DENIALED ACCESS PASSWORD */,
  '179': 'ASM_WRONG_ACCESS_TYPE' /* WRONG ACCESS TYPE */,
  '180': 'ASM_EXPIRED_ACCESS_PASSWORD' /* EXPIRED ACCESS PASSWORD */,
  '181': 'ASM_WRONG_ACCESS_PASSWORD' /* WRONG ACCESS PASSWORD */,
  '182': 'ASM_USER_ALREADY_LOCKED' /* USER ALREADY LOCKED */,
  '183': 'ASM_USER_PERMANENTLY_LOCKED' /* USER PERMANENTLY LOCKED */,
  '184': 'ASM_USER_ALREADY_UNLOCKED' /* USER ALREADY UNLOCKED */,
  '185': 'ASM_ACTIVE_ACCESS_PASSWORD' /* ACTIVE ACCESS PASSWORD */,
  '186': 'ASM_WRONG_HANDLE' /* WRONG HANDLE */,
  '187': 'ASM_UPD_IN_PROGRESS' /* UPD IN PROGRESS */,
  '188': 'ASM_GLOBAL_UPD_IN_PROGRESS' /* GLOBAL UPD IN PROGRESS */,
  '189': 'ASM_INVALID_PRODUCT_ID' /* INVALID PRODUCT ID */,
  '190': 'ASM_USER_NOT_ACTIVE' /* USER NOT ACTIVE */,
  '191': 'ASM_USER_DEACTIVATED' /* USER DEACTIVATED */,
  '192': 'ASM_PASSWORD_NOT_SET' /* PASSWORD NOT SET */,
  '193': 'ASM_INVALID_PERSONAL_ID' /* INVALID PERSONAL ID */,
  '194': 'ASM_UNDER_AGE_PLAYER' /* UNDER AGE PLAYER */,
  '195': 'ASM_TOO_YOUNG' /* TOO YOUNG */,
  '196': 'ASM_PERSONAL_ID_MISSING' /* PERSONAL ID MISSING */,
  '197': 'ASM_NEGATIVE_WAGER_AMOUNT' /* NEGATIVE WAGER AMOUNT */,
  '198': 'ASM_WAGER_BUDGET_EXCEEDED' /* WAGER BUDGET EXCEEDED */,
  '199': 'ASM_OVER_WAGER_LIMIT' /* OVER WAGER LIMIT */,
  '200': 'ASM_OVER_LANGEN_LIMIT' /* OVER LANGEN LIMIT */,
  '201': 'ASM_OVER_SINGLE_LIMIT' /* OVER SINGLE LIMIT */,
  '202': 'ASM_OVER_DOUBLE_LIMIT' /* OVER DOUBLE LIMIT */,
  '203': 'ASM_OVER_TRIPLE_LIMIT' /* OVER TRIPLE LIMIT */,
  '204': 'ASM_INSERT_WINGEN_FAILED' /* INSERT WINGEN FAILED */,
  '205': 'ASM_HIGHWIN_NOT_FOUND' /* HIGHWIN NOT FOUND */,
  '206': 'ASM_MAX_READ_REQ_EXCEEDED' /* MAX READ REQ EXCEEDED */,
  '207': 'ASM_NO_SUCH_WAGER' /* NO SUCH WAGER */,
  '208': 'ASM_WRONG_WINSTATE' /* WRONG WINSTATE */,
  '209': 'ASM_UNABLE_TO_MAP_WINFILE' /* UNABLE TO MAP WINFILE */,
  '210': 'ASM_INVALID_WINFILE_HEADER' /* INVALID WINFILE HEADER */,
  '211': 'ASM_INVALID_WINFILE' /* INVALID WINFILE */,
  '212': 'ASM_WINSUM_DISCREPANCY' /* WINSUM DISCREPANCY */,
  '213': 'ASM_WINMGR_SUBMIT' /* WINMGR SUBMIT */,
  '214': 'ASM_WINPAYOUT_IN_PROGRESS' /* WINPAYOUT IN PROGRESS */,
  '215': 'ASM_CHKPOINT_IN_PROGRESS' /* CHKPOINT IN PROGRESS */,
  '216': 'ASM_WINSTATE_NOT_FOUND' /* WINSTATE NOT FOUND */,
  '217': 'ASM_WINPAYOUT_REQUESTED' /* WINPAYOUT REQUESTED */,
  '218': 'ASM_ACCUPD_REQUESTED' /* ACCUPD REQUESTED */,
  '219': 'ASM_WINPAYOUT_STARTED' /* WINPAYOUT STARTED */,
  '220': 'ASM_ACCUPD_STARTED' /* ACCUPD STARTED */,
  '221': 'ASM_WINPAYOUT_DONE' /* WINPAYOUT DONE */,
  '222': 'ASM_ACCUPD_DONE' /* ACCUPD DONE */,
  '223': 'ASM_NO_SUCH_WIN' /* NO SUCH WIN */,
  '224': 'ASM_SELECT_WINGEN_FAILED' /* SELECT WINGEN FAILED */,
  '225': 'ASM_UPDATE_WINGEN_FAILED' /* UPDATE WINGEN FAILED */,
  '226': 'ASM_SUBMIT_FAILED' /* SUBMIT FAILED */,
  '227': 'ASM_WRONG_PRIV_LEVEL' /* WRONG PRIV LEVEL */,
  '228': 'ASM_WRONG_USER_TYPE' /* WRONG USER TYPE */,
  '229': 'ASM_MESSAGE_NOT_SUPPORTED' /* MESSAGE NOT SUPPORTED */,
  '230': 'ASM_WRONG_USER_ID' /* WRONG USER ID */,
  '231': 'ASM_SESSION_NOT_ACTIVE' /* SESSION NOT ACTIVE */,
  '232': 'ASM_INPUT_STRING_TOO_LONG' /* INPUT STRING TOO LONG */,
  '233': 'ASM_USER_NOT_FOUND' /* USER NOT FOUND */,
  '234': 'ASM_NO_FREE_USER_IN_LIST' /* NO FREE USER IN LIST */,
  '235': 'ASM_DIFFERENT_USERS' /* DIFFERENT USERS */,
  '236': 'ASM_NO_PRIVILEGE' /* NO PRIVILEGE */,
  '237': 'ASM_REQUEST_IN_PROGRESS' /* REQUEST IN PROGRESS */,
  '238': 'ASM_WRONG_USER_CHECKSUM' /* WRONG USER CHECKSUM */,
  '239': 'ASM_WRONG_TARGET_USER_CHECKSUM' /* WRONG TARGET USER CHECKSUM */,
  '240': 'ASM_WRONG_TARGET_USER_TYPE' /* WRONG TARGET USER TYPE */,
  '241': 'ASM_IMPERSONATED_USER' /* IMPERSONATED USER */,
  '242': 'ASM_WRONG_SOURCE' /* WRONG SOURCE */,
  '243': 'ASM_WRONG_OLD_PASSWORD' /* WRONG OLD PASSWORD */,
  '244': 'ASM_TARGET_USER_NOT_FOUND' /* TARGET USER NOT FOUND */,
  '245': 'ASM_TARGET_USER_ALREADY_EXISTS' /* TARGET USER ALREADY EXISTS */,
  '246': 'ASM_ALIAS_ALREADY_EXISTS' /* ALIAS ALREADY EXISTS */,
  '247': 'ASM_PENDING_BANKCARD_EXISTS' /* PENDING BANKCARD EXISTS */,
  '248': 'ASM_INPUT_STRING_TOO_SHORT' /* INPUT STRING TOO SHORT */,
  '249': 'ASM_INVALID_STATUS_CHANGE' /* INVALID STATUS CHANGE */,
  '250': 'ASM_PRIV_LEVEL_NOT_CHANGED' /* PRIV LEVEL NOT CHANGED */,
  '251': 'ASM_WRONG_OPENHOURS_DATE' /* WRONG OPENHOURS DATE */,
  '252': 'ASM_WRONG_OPENHOURS_MEDIA' /* WRONG OPENHOURS MEDIA */,
  '253': 'ASM_WRONG_OPENHOURS_NUM_ITEMS' /* WRONG OPENHOURS NUM ITEMS */,
  '254': 'ASM_WRONG_OPENHOURS_TIME' /* WRONG OPENHOURS TIME */,
  '255': 'ASM_INSERT_ACCUPD_FAILED' /* INSERT ACCUPD FAILED */,
  '256': 'ASM_UPDATE_ACCUPD_FAILED' /* UPDATE ACCUPD FAILED */,
  '257': 'ASM_SELECT_ACCUPD_FAILED' /* SELECT ACCUPD FAILED */,
  '258': 'ASM_ACCUPDSTATE_NOT_FOUND' /* ACCUPDSTATE NOT FOUND */,
  '259': 'ASM_WRONG_ACCUPDSTATE' /* WRONG ACCUPDSTATE */,
  '260': 'ASM_ACCUPD_IN_PROGRESS' /* ACCUPD IN PROGRESS */,
  '261': 'ASM_TARGET_USER_LOGGED_ON' /* TARGET USER LOGGED ON */,
  '262': 'ASM_SAME_USER' /* SAME USER */,
  '263': 'ASM_PASSWORD_NOT_VERIFIED' /* PASSWORD NOT VERIFIED */,
  '264': 'ASM_UNDER_AGE_CASINO' /* UNDER AGE CASINO */,
  '265': 'ASM_PERSONAL_ID_ALREADY_EXISTS' /* PERSONAL ID ALREADY EXISTS */,
  '266': 'ASM_ALIAS_NOT_FOUND' /* ALIAS NOT FOUND */,
  '267': 'ASM_SOURCE_NOT_ACTIVE' /* SOURCE NOT ACTIVE */,
  '268': 'ASM_BANK_MANAGER_NOT_ACTIVE' /* BANK MANAGER NOT ACTIVE */,
  '269': 'ASM_MONEVENT_NOT_FOUND' /* MONEVENT NOT FOUND */,
  '270': 'ASM_SAME_MONSTATE' /* SAME MONSTATE */,
  '271': 'ASM_INCORRECT_MONSTATE_CHANGE' /* INCORRECT MONSTATE CHANGE */,
  '272': 'ASM_USER_TERMINATED' /* USER TERMINATED */,
  '273': 'ASM_TARGET_USER_NOT_ACTIVE' /* TARGET USER NOT ACTIVE */,
  '274': 'ASM_TARGET_USER_DEACTIVATED' /* TARGET USER DEACTIVATED */,
  '275': 'ASM_TARGET_USER_TERMINATED' /* TARGET USER TERMINATED */,
  '276': 'ASM_ABOVE_WEEKLY_LIMIT' /* ABOVE WEEKLY LIMIT */,
  '277': 'ASM_ABOVE_MONTHLY_LIMIT' /* ABOVE MONTHLY LIMIT */,
  '278': 'ASM_BALANCE_ABOVE_DEPOSIT_LIMIT' /* BALANCE ABOVE DEPOSIT LIMIT */,
  '279': 'ASM_WRONG_PARAMETER_VALUE' /* WRONG PARAMETER VALUE */,
  '280': 'ASM_USER_BLOCKED' /* USER BLOCKED */,
  '281': 'ASM_WRONG_SURPLUS_TYPE' /* WRONG SURPLUS TYPE */,
  '282': 'ASM_WRONG_SYNDICATE_CHECKSUM' /* WRONG SYNDICATE CHECKSUM */,
  '283': 'ASM_WRONG_SYNDICATE_SEQ_NUM' /* WRONG SYNDICATE SEQ NUM */,
  '284': 'ASM_WRONG_RETRY_INFORMATION' /* WRONG RETRY INFORMATION */,
  '285': 'ASM_WRONG_SYNDICATE_ID' /* WRONG SYNDICATE ID */,
  '286': 'ASM_WRONG_TRANS_TYPE' /* WRONG TRANS TYPE */,
  '287': 'ASM_INCORRECT_DEPOSIT_BUDGET' /* INCORRECT DEPOSIT BUDGET */,
  '288': 'ASM_DEPOSIT_BUDGET_EXCEEDED' /* DEPOSIT BUDGET EXCEEDED */,
  '289': 'ASM_DEPOSIT_BUDGET_NOT_SET' /* DEPOSIT BUDGET NOT SET */,
  '290': 'ASM_WRONG_BANK_TYPE' /* WRONG BANK TYPE */,
  '291': 'ASM_NO_SLOTS_AVAILABLE' /* NO SLOTS AVAILABLE */,
  '292': 'ASM_INCORRECT_XFER_DATA' /* INCORRECT XFER DATA */,
  '293': 'ASM_WRONG_SLOT_STATUS' /* WRONG SLOT STATUS */,
  '294': 'ASM_WRONG_EXPIRE_DATE' /* WRONG EXPIRE DATE */,
  '295': 'ASM_WRONG_CARD_DIGITS' /* WRONG CARD DIGITS */,
  '296': 'ASM_BANKCARD_NOT_FOUND' /* BANKCARD NOT FOUND */,
  '297': 'ASM_PENDING_TRANSACTION' /* PENDING TRANSACTION */,
  '298': 'ASM_USER_NAME_NOT_FOUND' /* USER NAME NOT FOUND */,
  '299': 'ASM_USER_NAME_ALREADY_EXISTS' /* USER NAME ALREADY EXISTS */,
  '300': 'ASM_USER_NAME_INVALID' /* INVALID USER NAME */,
  '301': 'ASM_PASSWORD_NO_LETTER' /* PASSWORD NO LETTER */,
  '302': 'ASM_PASSWORD_BIRTHDATE' /* PASSWORD BIRTHDATE */,
  '303': 'ASM_PASSWORD_SSN_LAST4' /* PASSWORD SSN LAST 4 */,
  '304': 'ASM_PASSWORD_SEQ_ALPHA' /* PASSWORD SEQ ALPHA */,
  '305': 'ASM_PASSWORD_SEQ_DIGIT' /* PASSWORD SEQ DIGIT */,
  '306': 'ASM_PASSWORD_SAME_CHARS' /* PASSWORD SAME CHARS */,
  '307': 'ASM_PASSWORD_USERNAME' /* PASSWORD USERNAME */,
  '308': 'ASM_PASSWORD_FIRST_NAME' /* PASSWORD FIRST NAME */,
  '309': 'ASM_PASSWORD_LAST_NAME' /* PASSWORD LAST NAME */,
  '310': 'ASM_USER_NAME_NO_LETTER' /* USER NAME NO LETTER */,
  '311': 'ASM_BACKUP_IN_PROGRESS' /* BACKUP IN PROGRESS */,
  '312': 'ASM_PURGE_IN_PROGRESS' /* PURGE IN PROGRESS */,
  '313': 'ASM_PURGE_NOT_IN_PROGRESS' /* PURGE NOT IN PROGRESS */,
  '314': 'ASM_WRONG_RETRANSMIT_DATA' /* WRONG RETRANSMIT DATA */,
  '315': 'ASM_WRONG_SEQUENCE_NUM' /* WRONG SEQUENCE NUM */,
  '316': 'ASM_WRONG_NUM_RECORDS' /* WRONG NUM RECORDS */,
  '317': 'ASM_WRONG_TOTAL_AMOUNT' /* WRONG TOTAL AMOUNT */,
  '318': 'ASM_WRONG_PURGETYPE' /* WRONG PURGETYPE */,
  '319': 'ASM_WRONG_ITSMODULE' /* WRONG ITSMODULE */,
  '320': 'ASM_SUBSCRIPTION_ALR_EXISTS' /* SUBSCRIPTION ALREADY EXISTS */,
  '321': 'ASM_SUBSCRIPTION_NOT_FOUND' /* SUBSCRIPTION NOT FOUND */,
  '322': 'ASM_SUBSCRIPTION_USER_MISMATCH' /* SUBSCRIPTION USER MISMATCH */,
  '323': 'ASM_SUBSCRIPTION_NOT_WINNER' /* SUBSCRIPTION NOT WINNER */,
  '324': 'ASM_REVIEW_AMOUNT_EXCEEDED' /* REVIEW AMOUNT EXCEEDED */,
  '325': 'ASM_POKER_SUSPENDED' /* POKER SUSPENDED */,
  '326': 'ASM_SYNDICATES_SUSPENDED' /* SYNDICATES SUSPENDED */,
  '327': 'ASM_SUBSCRIPTIONS_SUSPENDED' /* SUBSCRIPTIONS SUSPENDED */,
  '328': 'ASM_BANK_XFERS_SUSPENDED' /* BANK XFERS SUSPENDED */,
  '329': 'ASM_WRONG_PINCODE' /* WRONG PINCODE */,
  '330': 'ASM_PINCODE_NOT_SET' /* PINCODE NOT SET */,
  '331': 'ASM_PINCODE_ERROR_EXCEEDED' /* PINCODE ERROR EXCEEDED */,
  '332': 'ASM_KYC_SELL_DISABLED' /* KYC SELL DISABLED */,
  '333': 'ASM_DEPOSIT_DAY_LIMIT_EXCEEDED' /* DEPOSIT DAY LIMIT EXCEEDED */,
  '334': 'ASM_DEPOSIT_WEEK_LIMIT_EXCEEDED' /* DEPOSIT WEEK LIMIT EXCEEDED */,
  '335': 'ASM_DEPOSIT_MONTH_LIMIT_EXCEEDED' /* DEPOSIT MONTH LIMIT EXCEEDED */,
  '336': 'ASM_DAY_TIME_DISABLED' /* DAY TIME DISABLED */,
  '337': 'ASM_LIMITS_LOCKED' /* LIMITS LOCKED */,
} as const;
type ITS_ASM_NUMERIC = keyof typeof ITS_ASM;
type ITS_ASM = (typeof ITS_ASM)[ITS_ASM_NUMERIC];

const ITS_RET = {} as const;
type ITS_RET_NUMERIC = keyof typeof ITS_RET;
type ITS_RET = (typeof ITS_RET)[ITS_RET_NUMERIC];

const ITS_GAME = {
  '3': 'GAME_IMPOSSIBLE_ROWS',
  '103': 'GAME_LAST_MINUTE_CHANGE',
  '104': 'GAME_BAD_DRAWNUMBER',
  '105': 'GAME_BAD_DRAWSTATE',
  '107': 'GAME_DRAW_NOT_OPENED',
  '122': 'GAME_CANCELATION_STOPPED',
  '124': 'GAME_REGBET_DISABLED',
  '171': 'GAME_CANCEL_TIME_EXCEEDED',
  '199': 'GAME_CANCELATION_RETAILER_NOT_POSSIBLE',
  '200': 'GAME_CANCEL_LIVESPEL',
  '201': 'GAME_UNABLE_TO_START_WINPAYOUT',
  '202': 'GAME_MAX_ROW_WINAMOUNT_EXCEEDED',
  '203': 'GAME_FUND_IS_BUSY',
  '204': 'GAME_BAD_RESULT_COMBINATION',
  '205': 'GAME_CANCEL_SUBSCRIPTION_BET',
  '206': 'GAME_WINGEN_DISCREPANCY',
  '207': 'GAME_PRODUCT_CLOSED',
  '208': 'GAME_EVENT_REPLACED',
  '209': 'GAME_LIMIT_ALREADY_DEFINED',
  '210': 'GAME_BAD_NUMEVENTS_IN_BET',
  '211': 'GAME_REGBET_IN_PROGRESS',
  '212': 'GAME_UNBETTABLE_ODDS',
  '213': 'GAME_UNBETTABLE_SCORECAST_COMB',
  '214': 'GAME_OUTCOME_CANCELLED',
  '215': 'GAME_BAD_LIVEBET_COMB',
  '216': 'GAME_LIVEBET_TOO_EARLY',
  '217': 'GAME_BAD_LIVEBET_CONTEXT',
  '218': 'GAME_LIVEBET_TIMEOUT',
  '219': 'GAME_CANCEL_NOT_ALLOWED',
  '220': 'GAME_FRAC_DISCREPANCY',
  '221': 'GAME_REQUEST_TOO_EARLY',
  '222': 'GAME_CUST_MAX_DRAW_REGAMT',
  '223': 'GAME_CUST_MAX_DRAW_EGNARADER_REGAMT',
  '224': 'GAME_CUST_MAX_DAILY_PRODUCT_REGAMT',
  '225': 'GAME_SYNDICATE_MAX_DRAW_REGAMT',
  '226': 'GAME_SYNDICATE_MAX_DRAW_EGNARADER_REGAMT',
  '227': 'GAME_SYNDICATE_MAX_DAILY_PRODUCT_REGAMT',
  '228': 'GAME_RETAILER_MAX_DRAW_REGAMT',
  '229': 'GAME_RETAILER_MAX_DRAW_ANDELSSPEL_REGAMT',
  '230': 'GAME_RETAILER_MAX_DAILY_PRODUCT_REGAMT',
  '231': 'GAME_ODDS_TOO_LOW',
  '232': 'GAME_ODDS_TOO_HIGH',
  '233': 'GAME_BET_AMOUNT_TOO_LOW',
  '234': 'GAME_BET_AMOUNT_TOO_HIGH',
  '235': 'GAME_CUST_MAX_DAILY_LIVE_REGAMT',
  '236': 'GAME_BAD_DEFSRC',
  '237': 'GAME_UNSUPPORTED_EVENTDEF',
  '238': 'GAME_LIVE_DRAW_CONFLICT',
  '239': 'GAME_CUST_MAX_BOARDS_IN_DRAW',
  '240': 'GAME_SYNDICATE_MAX_BOARDS_IN_DRAW',
  '241': 'GAME_TOO_MANY_FAILED_EVENTS',
  '242': 'GAME_TOO_MANY_EVENTS_IN_DRAW',
  '243': 'GAME_DRAW_CLOSED_BY_OWN',
  '244': 'GAME_DRAW_CLOSED_BY_ODDSIMP',
  '245': 'GAME_UNABLE_TO_ARCHIVE',
  '246': 'GAME_BETAMT_INCLUDES_IMPROWS',
  '247': 'GAME_BET_CONTAINS_ONLY_IMPROWS',
  '248': 'GAME_BET_AMOUNT_TOO_LOW_AFTER_IMPROW_RED',
  '249': 'GAME_BAD_AUDITSTATE',
  '250': 'GAME_BAD_AUDITSERIAL',
  '251': 'GAME_CUSTSUP_MAX_SINGLE_BETS_EXCEEDED',
  '252': 'GAME_BAD_RANKING_LIST',
  '253': 'GAME_FAILED_EXT_REGBET_REQUEST',
  '254': 'GAME_FAILED_EXT_CANCELBET_REQUEST',
  '255': 'GAME_TPPROXY_TIMEOUT',
  '256': 'GAME_BAD_RACE_STATE',
  '257': 'GAME_BAD_BET_STATE',
  '258': 'GAME_BET_ALREADY_COMPLETE',
  '259': 'GAME_BAD_REFUND_AMOUNT',
  '260': 'GAME_OPENHOURS_CONFLICT_WITH_NEXT_DRAW',
  '261': 'GAME_OPENHOURS_CONFLICT_WITH_PREV_DRAW',
  '262': 'GAME_BAD_WINCOUNT_TIME',
  '263': 'GAME_BAD_START_DATE',
  '264': 'GAME_BAD_STOP_DATE',
  '265': 'GAME_DATE_CONFLICT_WITH_OTHER_DRAW',
  '266': 'GAME_INCORRECT_NUM_PART_DRAWS',
  '267': 'GAME_BAD_DRAWTYPE',
  '268': 'GAME_BAD_PARTICIPATION_TERMS',
  '269': 'GAME_BAD_WINTYPE',
  '270': 'GAME_INCORRECT_DIV_WINNERS',
  '271': 'GAME_INCORRECT_TOT_WINNERS',
  '272': 'GAME_DATE_CONFLICT_BETWEEN_RCV_INTERVALS',
  '273': 'GAME_INCORRECT_TOT_WINAMOUNT',
  '274': 'GAME_WINCOUNT_TOO_EARLY',
  '275': 'GAME_SPELADMIN_REQUEST_MAXTIME',
} as const;
type ITS_GAME_NUMERIC = keyof typeof ITS_GAME;
type ITS_GAME = (typeof ITS_GAME)[ITS_GAME_NUMERIC];

const ITS_SCORE = {} as const;
type ITS_SCORE_NUMERIC = keyof typeof ITS_SCORE;
type ITS_SCORE = (typeof ITS_SCORE)[ITS_SCORE_NUMERIC];

const ITS_MCAST = {} as const;
type ITS_MCAST_NUMERIC = keyof typeof ITS_MCAST;
type ITS_MCAST = (typeof ITS_MCAST)[ITS_MCAST_NUMERIC];

const ITS_CAMP = {} as const;
type ITS_CAMP_NUMERIC = keyof typeof ITS_CAMP;
type ITS_CAMP = (typeof ITS_CAMP)[ITS_CAMP_NUMERIC];

const ITS_VAL = {} as const;
type ITS_VAL_NUMERIC = keyof typeof ITS_VAL;
type ITS_VAL = (typeof ITS_VAL)[ITS_VAL_NUMERIC];

const ITS_LOTT = {} as const;
type ITS_LOTT_NUMERIC = keyof typeof ITS_LOTT;
type ITS_LOTT = (typeof ITS_LOTT)[ITS_LOTT_NUMERIC];

const ITS_SM = {} as const;
type ITS_SM_NUMERIC = keyof typeof ITS_SM;
type ITS_SM = (typeof ITS_SM)[ITS_SM_NUMERIC];

const ITS_LM = {} as const;
type ITS_LM_NUMERIC = keyof typeof ITS_LM;
type ITS_LM = (typeof ITS_LM)[ITS_LM_NUMERIC];

const ITS_TSM = {} as const;
type ITS_TSM_NUMERIC = keyof typeof ITS_TSM;
type ITS_TSM = (typeof ITS_TSM)[ITS_TSM_NUMERIC];

const ITS_DCACHE = {} as const;
type ITS_DCACHE_NUMERIC = keyof typeof ITS_DCACHE;
type ITS_DCACHE = (typeof ITS_DCACHE)[ITS_DCACHE_NUMERIC];

const ITS_WIN = {} as const;
type ITS_WIN_NUMERIC = keyof typeof ITS_WIN;
type ITS_WIN = (typeof ITS_WIN)[ITS_WIN_NUMERIC];

const ITS_TSTORE = {} as const;
type ITS_TSTORE_NUMERIC = keyof typeof ITS_TSTORE;
type ITS_TSTORE = (typeof ITS_TSTORE)[ITS_TSTORE_NUMERIC];

const ITS_MBVAL = {} as const;
type ITS_MBVAL_NUMERIC = keyof typeof ITS_MBVAL;
type ITS_MBVAL = (typeof ITS_MBVAL)[ITS_MBVAL_NUMERIC];

const ITS_OMS = {} as const;
type ITS_OMS_NUMERIC = keyof typeof ITS_OMS;
type ITS_OMS = (typeof ITS_OMS)[ITS_OMS_NUMERIC];

const ITS_SYN = {} as const;
type ITS_SYN_NUMERIC = keyof typeof ITS_SYN;
type ITS_SYN = (typeof ITS_SYN)[ITS_SYN_NUMERIC];

const ITS_BRC = {} as const;
type ITS_BRC_NUMERIC = keyof typeof ITS_BRC;
type ITS_BRC = (typeof ITS_BRC)[ITS_BRC_NUMERIC];

const ITS_SUB = {} as const;
type ITS_SUB_NUMERIC = keyof typeof ITS_SUB;
type ITS_SUB = (typeof ITS_SUB)[ITS_SUB_NUMERIC];

const ITS_PAY = {} as const;
type ITS_PAY_NUMERIC = keyof typeof ITS_PAY;
type ITS_PAY = (typeof ITS_PAY)[ITS_PAY_NUMERIC];

const ITS_VOM = {} as const;
type ITS_VOM_NUMERIC = keyof typeof ITS_VOM;
type ITS_VOM = (typeof ITS_VOM)[ITS_VOM_NUMERIC];

const ITS_COMP = {
  '3': 'COMP_INCORRECT_PARAMETER' /* INCORRECT_PARAMETER */,
  '101': 'COMP_COMPETITION_NOT_FOUND' /* COMPETITION NOT FOUND */,
  '102': 'COMP_ACTIVE_COMPETITION_NOT_OK' /* ACTIVE COMPETITION NOT OK */,
  '106': 'COMP_SIGNUP_TO_EARLY' /* SIGNUP TO EARLY */,
  '103': 'COMP_ACTIVE_BET_NOT_OK' /* ACTIVE BET NOT OK */,
} as const;
type ITS_COMP_NUMERIC = keyof typeof ITS_COMP;
type ITS_COMP = (typeof ITS_COMP)[ITS_COMP_NUMERIC];

const ITS_PACC = {
  '0': 'PACC_NOT_USED' /* NOT_USED */,
  '1': 'PACC_ERROR' /* ERROR */,
  '2': 'PACC_INVALID_MSG_ID' /* INVALID MSG ID */,
  '3': 'PACC_INCORRECT_PARAMETER' /* INCORRECT PARAMETER */,
  '4': 'PACC_TIMEOUT' /* TIMEOUT */,
  '5': 'PACC_MODULE_NOT_AVAILABLE' /* MODULE NOT AVAILABLE */,
  '6': 'PACC_FILE_ERROR' /* FILE_ERROR */,
  '7': 'PACC_NO_DATA_FOUND' /* NO_DATA_FOUND */,
  '8': 'PACC_INVALID_MSG_VERSION' /* INVALID_MSG_VERSION */,
  '9': 'PACC_SIZE_ERROR' /* SIZE_ERROR */,
  '10': 'PACC_NO_IPC_BUFFERS' /* NO_IPC_BUFFERS_AVAIL */,
  '11': 'PACC_NO_PRIVILEGE' /* NO_PRIVILEGE */,
  '12': 'PACC_PROCESS_IS_BUSY' /* PROCESS_IS_BUSY */,
  '13': 'PACC_INVALID_PROTOCOL_VERSION' /* INVALID PROTOCOL VERSION */,
  '14': 'PACC_NATIONAL_BLOCKED' /* NATIONAL BLOCKED */,
  '101': 'PACC_CREATE_RESERVE_ID' /* CREATE_RESERVE_ID */,
  '102': 'PACC_UPDATE_RESERVE_ID' /* UPDATE_RESERVE_ID */,
  '103': 'PACC_HTTP_ERROR' /* HTTP_ERROR */,
  '104': 'PACC_PARSE_RESPONSE' /* PARSE_RESPONSE */,
  '105': 'PACC_TECHNICAL_ERROR_SPELPAUS' /* TECHNICAL_PAMERROR AND SPELPAUS */,
  '106': 'PACC_NOT_ENOUGH_MONEY' /* NOT_ENOUGH_MONEY */,
  '107': 'PACC_USER_NOT_FOUND' /* USER_NOT_FOUND */,
  '108': 'PACC_USER_BLOCKED' /* USER_BLOCKED */,
  '109': 'PACC_LIMIT_REACHED' /* LIMIT_REACHED */,
  '110': 'PACC_WRONG_TARGET_USER' /* WRONG_TARGET_USER */,
  '111': 'PACC_WRONG_RESERVE_ID' /* WRONG_RESERVE_ID */,
  '112': 'PACC_WRONG_PAM_AMOUNT' /* WRONG_PAM_AMOUNT */,
} as const;
type ITS_PACC_NUMERIC = keyof typeof ITS_PACC;
type ITS_PACC = (typeof ITS_PACC)[ITS_PACC_NUMERIC];

const ITS_TACC = {};
type ITS_TACC_NUMERIC = keyof typeof ITS_TACC;
type ITS_TACC = (typeof ITS_TACC)[ITS_TACC_NUMERIC];

const ITS_RES = {};
type ITS_RES_NUMERIC = keyof typeof ITS_RES;
type ITS_RES = (typeof ITS_RES)[ITS_RES_NUMERIC];

const ITS_INVALID_FACILITY = {};
type ITS_INVALID_FACILITY_NUMERIC = keyof typeof ITS_INVALID_FACILITY;
type ITS_INVALID_FACILITY = (typeof ITS_INVALID_FACILITY)[ITS_INVALID_FACILITY_NUMERIC];

const ITS_NONE = {
  '41': 'MARKETPLACE_WAGER_ALREADY_PAID' /* WAGER ALREADY PAID */,
} as const;
type ITS_NONE_NUMERIC = keyof typeof ITS_NONE;
type ITS_NONE = (typeof ITS_NONE)[ITS_NONE_NUMERIC];

export type ITS_ERROR =
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  | ITS_NOT_USED
  | ITS_NOT_USED2
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_AGENT
  | ITS_GATEWAY
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_RNGW
  | ITS_TM
  | ITS_ASM
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_RET
  | ITS_GAME
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_SCORE
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_MCAST
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_CAMP
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_VAL
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_LOTT
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_SM
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_LM
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_TSM
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_DCACHE
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_WIN
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_TSTORE
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_MBVAL
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_OMS
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_SYN
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_BRC
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_SUB
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_PAY
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents, @typescript-eslint/no-redundant-type-constituents
  | ITS_VOM
  | ITS_COMP
  | ITS_PACC
  | ITS_NONE;
type ClientITSError = Record<ITS_ERROR, ClientErrorCode>;

const ITS_ERROR: ClientITSError = {
  NOT_USED2_SUSPENSION: 'GENERIC',
  TAS_UNAVAILABLE: 'PAYMENT_REQUEST_FAILURE',
  TM_GENERIC: 'GENERIC',
  TM_GENERIC2: 'SUSPENSION',
  ASM_INVALID_HANDLE: 'GENERIC',
  ASM_WRONG_ALIAS_LENGTH: 'GENERIC',
  ASM_NUM_ERROR_BANKCARD: 'GENERIC',
  ASM_WRONG_PASSWORD: 'GENERIC',
  ASM_USER_ALREADY_EXISTS: 'GENERIC',
  ASM_EXPIRE_DATE_LENGTH: 'GENERIC',
  ASM_CARD_NUMBER_LENGTH: 'GENERIC',
  ASM_INSERT_ACCHIST_FAILED: 'GENERIC',
  ASM_UPDATE_BALANCE_FAILED: 'GENERIC',
  ASM_NOPRIV: 'GENERIC',
  ASM_WRONG_SERIAL_NUM: 'GENERIC',
  ASM_PASSWORD_LOCK: 'GENERIC',
  ASM_NO_PRIV: 'GENERIC',
  ASM_CHANGE_PASSWORD_REQUIRED: 'GENERIC',
  ASM_WRONG_CHECKSUM: 'GENERIC',
  ASM_USER_NOT_INTERNAL: 'GENERIC',
  ASM_USER_NOT_EXTERNAL: 'GENERIC',
  ASM_OWNERSHIP_FAILURE: 'GENERIC',
  ASM_OTHER_USER_ACCESSED: 'GENERIC',
  ASM_TARGET_SESSION_NOT_ACTIVE: 'GENERIC',
  ASM_NOT_ENOUGH_MONEY: 'GENERIC',
  ASM_CUSTOMERNOTAUTHORIZED: 'GENERIC',
  ASM_WRONG_USER_GROUP: 'GENERIC',
  ASM_PASSWORD_NOT_CHANGED: 'GENERIC',
  ASM_PASSWORD_LENGTH: 'GENERIC',
  ASM_PASSWORD_NO_DIGIT: 'GENERIC',
  ASM_PASSWORD_SAME_DIGITS: 'GENERIC',
  ASM_PASSWORD_SEQUENCE: 'GENERIC',
  ASM_PASSWORD_PERSONAL_ID: 'GENERIC',
  ASM_PASSWORD_USERID: 'GENERIC',
  ASM_NO_SUCH_PROPERTY: 'GENERIC',
  ASM_PROPERTY_SET: 'GENERIC',
  ASM_PROPERTY_CLEARED: 'GENERIC',
  ASM_CLOSE_TOO_LATE: 'GENERIC',
  ASM_OPEN_CLOSE_MISMATCH: 'GENERIC',
  ASM_ADJUST_CUSTOMER_ACCOUNT: 'GENERIC',
  ASM_MONTRANS_FEE_NOT_ALLOWED: 'GENERIC',
  ASM_WRONG_MONTRANS_REF_NUM: 'GENERIC',
  ASM_WRONG_MONTRANS_REASON: 'GENERIC',
  ASM_WRONG_MONTRANS_TYPE: 'GENERIC',
  ASM_WRONG_MONTRANS_REQ_TYPE: 'GENERIC',
  ASM_WRONG_MONTRANS_CUSTOMER: 'GENERIC',
  ASM_WRONG_MONTRANS_SEQ_NUM: 'GENERIC',
  ASM_WRONG_MONTRANS_AMOUNT: 'GENERIC',
  ASM_WRONG_MONTRANS_FEE: 'GENERIC',
  ASM_BELOW_LIMIT: 'GENERIC',
  ASM_ABOVE_LIMIT: 'GENERIC',
  ASM_FUNCTION_CLOSED: 'GENERIC',
  ASM_NO_ACCOUNT_USAGE: 'PLAYER_PAUSED',
  ASM_ABORTED_SEQ_NUM: 'GENERIC',
  ASM_NO_FREE_IBANKIDX: 'GENERIC',
  ASM_REF_NUM_NOT_ALLOWED: 'GENERIC',
  ASM_REF_NUM_MISSING: 'GENERIC',
  ASM_DRAW_LIMIT_REACHED: 'GENERIC',
  ASM_INCORRECT_WAGER_BUDGET: 'GENERIC',
  ASM_PROPERTY_CHANGE: 'GENERIC',
  ASM_USER_GROUP_NOT_CHANGED: 'GENERIC',
  ASM_TIMEOUT: 'GENERIC',
  ASM_FUNCTION_CLOSED_DAYTIME: 'GENERIC',
  ASM_BANKCARD_NOT_ALLOWED: 'GENERIC',
  ASM_PENDING_LIMIT_REACHED: 'GENERIC',
  ASM_UNABLE_TO_PROCESS: 'GENERIC',
  ASM_MERCHANT_NOT_ON_FILE: 'GENERIC',
  ASM_INVALID_TRANSACTION: 'GENERIC',
  ASM_INVALID_CARD_NO: 'GENERIC',
  ASM_EXPIRED_CARD: 'GENERIC',
  ASM_RESTRICTED_CARD_PICK_UP: 'GENERIC',
  ASM_STOLEN_CARD_PICK_UP: 'GENERIC',
  ASM_NOT_SUFFICIENT_FUNDS: 'GENERIC',
  ASM_EXCEEDS_AMOUNT_LIMIT: 'GENERIC',
  ASM_DUPLICATE_TRANSACTION: 'GENERIC',
  ASM_UNABLE_TO_AUTHORIZE: 'GENERIC',
  ASM_ABOVE_DAILY_LIMIT: 'GENERIC',
  ASM_CARD_NOT_SUPPORTED: 'GENERIC',
  ASM_BANKACCOUNT_NOT_ALLOWED: 'GENERIC',
  ASM_PENDING_BANKCARD: 'GENERIC',
  ASM_NO_ACTIVE_ACCESS_PASSWORD: 'GENERIC',
  ASM_DENIALED_ACCESS_PASSWORD: 'GENERIC',
  ASM_WRONG_ACCESS_TYPE: 'GENERIC',
  ASM_EXPIRED_ACCESS_PASSWORD: 'GENERIC',
  ASM_WRONG_ACCESS_PASSWORD: 'GENERIC',
  ASM_USER_ALREADY_LOCKED: 'GENERIC',
  ASM_USER_PERMANENTLY_LOCKED: 'GENERIC',
  ASM_USER_ALREADY_UNLOCKED: 'GENERIC',
  ASM_ACTIVE_ACCESS_PASSWORD: 'GENERIC',
  ASM_WRONG_HANDLE: 'GENERIC',
  ASM_UPD_IN_PROGRESS: 'GENERIC',
  ASM_GLOBAL_UPD_IN_PROGRESS: 'GENERIC',
  ASM_INVALID_PRODUCT_ID: 'GENERIC',
  ASM_USER_NOT_ACTIVE: 'GENERIC',
  ASM_USER_DEACTIVATED: 'GENERIC',
  ASM_PASSWORD_NOT_SET: 'GENERIC',
  ASM_INVALID_PERSONAL_ID: 'GENERIC',
  ASM_UNDER_AGE_PLAYER: 'GENERIC',
  ASM_TOO_YOUNG: 'GENERIC',
  ASM_PERSONAL_ID_MISSING: 'GENERIC',
  ASM_NEGATIVE_WAGER_AMOUNT: 'GENERIC',
  ASM_WAGER_BUDGET_EXCEEDED: 'GENERIC',
  ASM_OVER_WAGER_LIMIT: 'GENERIC',
  ASM_OVER_LANGEN_LIMIT: 'GENERIC',
  ASM_OVER_SINGLE_LIMIT: 'GENERIC',
  ASM_OVER_DOUBLE_LIMIT: 'GENERIC',
  ASM_OVER_TRIPLE_LIMIT: 'GENERIC',
  ASM_INSERT_WINGEN_FAILED: 'GENERIC',
  ASM_HIGHWIN_NOT_FOUND: 'GENERIC',
  ASM_MAX_READ_REQ_EXCEEDED: 'GENERIC',
  ASM_NO_SUCH_WAGER: 'GENERIC',
  ASM_WRONG_WINSTATE: 'GENERIC',
  ASM_UNABLE_TO_MAP_WINFILE: 'GENERIC',
  ASM_INVALID_WINFILE_HEADER: 'GENERIC',
  ASM_INVALID_WINFILE: 'GENERIC',
  ASM_WINSUM_DISCREPANCY: 'GENERIC',
  ASM_WINMGR_SUBMIT: 'GENERIC',
  ASM_WINPAYOUT_IN_PROGRESS: 'GENERIC',
  ASM_CHKPOINT_IN_PROGRESS: 'GENERIC',
  ASM_WINSTATE_NOT_FOUND: 'GENERIC',
  ASM_WINPAYOUT_REQUESTED: 'GENERIC',
  ASM_ACCUPD_REQUESTED: 'GENERIC',
  ASM_WINPAYOUT_STARTED: 'GENERIC',
  ASM_ACCUPD_STARTED: 'GENERIC',
  ASM_WINPAYOUT_DONE: 'GENERIC',
  ASM_ACCUPD_DONE: 'GENERIC',
  ASM_NO_SUCH_WIN: 'GENERIC',
  ASM_SELECT_WINGEN_FAILED: 'GENERIC',
  ASM_UPDATE_WINGEN_FAILED: 'GENERIC',
  ASM_SUBMIT_FAILED: 'GENERIC',
  ASM_WRONG_PRIV_LEVEL: 'GENERIC',
  ASM_WRONG_USER_TYPE: 'GENERIC',
  ASM_MESSAGE_NOT_SUPPORTED: 'GENERIC',
  ASM_WRONG_USER_ID: 'GENERIC',
  ASM_SESSION_NOT_ACTIVE: 'GENERIC',
  ASM_INPUT_STRING_TOO_LONG: 'GENERIC',
  ASM_USER_NOT_FOUND: 'GENERIC',
  ASM_NO_FREE_USER_IN_LIST: 'GENERIC',
  ASM_DIFFERENT_USERS: 'GENERIC',
  ASM_NO_PRIVILEGE: 'GENERIC',
  ASM_REQUEST_IN_PROGRESS: 'GENERIC',
  ASM_WRONG_USER_CHECKSUM: 'GENERIC',
  ASM_WRONG_TARGET_USER_CHECKSUM: 'GENERIC',
  ASM_WRONG_TARGET_USER_TYPE: 'GENERIC',
  ASM_IMPERSONATED_USER: 'GENERIC',
  ASM_WRONG_SOURCE: 'GENERIC',
  ASM_WRONG_OLD_PASSWORD: 'GENERIC',
  ASM_TARGET_USER_NOT_FOUND: 'GENERIC',
  ASM_TARGET_USER_ALREADY_EXISTS: 'GENERIC',
  ASM_ALIAS_ALREADY_EXISTS: 'GENERIC',
  ASM_PENDING_BANKCARD_EXISTS: 'GENERIC',
  ASM_INPUT_STRING_TOO_SHORT: 'GENERIC',
  ASM_INVALID_STATUS_CHANGE: 'GENERIC',
  ASM_PRIV_LEVEL_NOT_CHANGED: 'GENERIC',
  ASM_WRONG_OPENHOURS_DATE: 'GENERIC',
  ASM_WRONG_OPENHOURS_MEDIA: 'GENERIC',
  ASM_WRONG_OPENHOURS_NUM_ITEMS: 'GENERIC',
  ASM_WRONG_OPENHOURS_TIME: 'GENERIC',
  ASM_INSERT_ACCUPD_FAILED: 'GENERIC',
  ASM_UPDATE_ACCUPD_FAILED: 'GENERIC',
  ASM_SELECT_ACCUPD_FAILED: 'GENERIC',
  ASM_ACCUPDSTATE_NOT_FOUND: 'GENERIC',
  ASM_WRONG_ACCUPDSTATE: 'GENERIC',
  ASM_ACCUPD_IN_PROGRESS: 'GENERIC',
  ASM_TARGET_USER_LOGGED_ON: 'GENERIC',
  ASM_SAME_USER: 'GENERIC',
  ASM_PASSWORD_NOT_VERIFIED: 'GENERIC',
  ASM_UNDER_AGE_CASINO: 'GENERIC',
  ASM_PERSONAL_ID_ALREADY_EXISTS: 'GENERIC',
  ASM_ALIAS_NOT_FOUND: 'GENERIC',
  ASM_SOURCE_NOT_ACTIVE: 'GENERIC',
  ASM_BANK_MANAGER_NOT_ACTIVE: 'GENERIC',
  ASM_MONEVENT_NOT_FOUND: 'GENERIC',
  ASM_SAME_MONSTATE: 'GENERIC',
  ASM_INCORRECT_MONSTATE_CHANGE: 'GENERIC',
  ASM_USER_TERMINATED: 'GENERIC',
  ASM_TARGET_USER_NOT_ACTIVE: 'GENERIC',
  ASM_TARGET_USER_DEACTIVATED: 'GENERIC',
  ASM_TARGET_USER_TERMINATED: 'GENERIC',
  ASM_ABOVE_WEEKLY_LIMIT: 'GENERIC',
  ASM_ABOVE_MONTHLY_LIMIT: 'GENERIC',
  ASM_BALANCE_ABOVE_DEPOSIT_LIMIT: 'GENERIC',
  ASM_WRONG_PARAMETER_VALUE: 'GENERIC',
  ASM_USER_BLOCKED: 'GENERIC',
  ASM_WRONG_SURPLUS_TYPE: 'GENERIC',
  ASM_WRONG_SYNDICATE_CHECKSUM: 'GENERIC',
  ASM_WRONG_SYNDICATE_SEQ_NUM: 'GENERIC',
  ASM_WRONG_RETRY_INFORMATION: 'GENERIC',
  ASM_WRONG_SYNDICATE_ID: 'GENERIC',
  ASM_WRONG_TRANS_TYPE: 'GENERIC',
  ASM_INCORRECT_DEPOSIT_BUDGET: 'GENERIC',
  ASM_DEPOSIT_BUDGET_EXCEEDED: 'GENERIC',
  ASM_DEPOSIT_BUDGET_NOT_SET: 'GENERIC',
  ASM_WRONG_BANK_TYPE: 'GENERIC',
  ASM_NO_SLOTS_AVAILABLE: 'GENERIC',
  ASM_INCORRECT_XFER_DATA: 'GENERIC',
  ASM_WRONG_SLOT_STATUS: 'GENERIC',
  ASM_WRONG_EXPIRE_DATE: 'GENERIC',
  ASM_WRONG_CARD_DIGITS: 'GENERIC',
  ASM_BANKCARD_NOT_FOUND: 'GENERIC',
  ASM_PENDING_TRANSACTION: 'GENERIC',
  ASM_USER_NAME_NOT_FOUND: 'GENERIC',
  ASM_USER_NAME_ALREADY_EXISTS: 'GENERIC',
  ASM_USER_NAME_INVALID: 'GENERIC',
  ASM_PASSWORD_NO_LETTER: 'GENERIC',
  ASM_PASSWORD_BIRTHDATE: 'GENERIC',
  ASM_PASSWORD_SSN_LAST4: 'GENERIC',
  ASM_PASSWORD_SEQ_ALPHA: 'GENERIC',
  ASM_PASSWORD_SEQ_DIGIT: 'GENERIC',
  ASM_PASSWORD_SAME_CHARS: 'GENERIC',
  ASM_PASSWORD_USERNAME: 'GENERIC',
  ASM_PASSWORD_FIRST_NAME: 'GENERIC',
  ASM_PASSWORD_LAST_NAME: 'GENERIC',
  ASM_USER_NAME_NO_LETTER: 'GENERIC',
  ASM_BACKUP_IN_PROGRESS: 'GENERIC',
  ASM_PURGE_IN_PROGRESS: 'GENERIC',
  ASM_PURGE_NOT_IN_PROGRESS: 'GENERIC',
  ASM_WRONG_RETRANSMIT_DATA: 'GENERIC',
  ASM_WRONG_SEQUENCE_NUM: 'GENERIC',
  ASM_WRONG_NUM_RECORDS: 'GENERIC',
  ASM_WRONG_TOTAL_AMOUNT: 'GENERIC',
  ASM_WRONG_PURGETYPE: 'GENERIC',
  ASM_WRONG_ITSMODULE: 'GENERIC',
  ASM_SUBSCRIPTION_ALR_EXISTS: 'GENERIC',
  ASM_SUBSCRIPTION_NOT_FOUND: 'GENERIC',
  ASM_SUBSCRIPTION_USER_MISMATCH: 'GENERIC',
  ASM_SUBSCRIPTION_NOT_WINNER: 'GENERIC',
  ASM_REVIEW_AMOUNT_EXCEEDED: 'GENERIC',
  ASM_POKER_SUSPENDED: 'GENERIC',
  ASM_SYNDICATES_SUSPENDED: 'GENERIC',
  ASM_SUBSCRIPTIONS_SUSPENDED: 'GENERIC',
  ASM_BANK_XFERS_SUSPENDED: 'GENERIC',
  ASM_WRONG_PINCODE: 'GENERIC',
  ASM_PINCODE_NOT_SET: 'GENERIC',
  ASM_PINCODE_ERROR_EXCEEDED: 'GENERIC',
  ASM_KYC_SELL_DISABLED: 'GENERIC',
  ASM_DEPOSIT_DAY_LIMIT_EXCEEDED: 'GENERIC',
  ASM_DEPOSIT_WEEK_LIMIT_EXCEEDED: 'GENERIC',
  ASM_DEPOSIT_MONTH_LIMIT_EXCEEDED: 'GENERIC',
  ASM_DAY_TIME_DISABLED: 'GENERIC',
  ASM_LIMITS_LOCKED: 'GENERIC',
  GAME_IMPOSSIBLE_ROWS: 'IMPOSSIBLE_ROWS',
  GAME_LAST_MINUTE_CHANGE: 'BAD_DRAW',
  GAME_BAD_DRAWNUMBER: 'BAD_DRAW',
  GAME_BAD_DRAWSTATE: 'BAD_DRAW',
  GAME_DRAW_NOT_OPENED: 'BAD_DRAW',
  GAME_CANCELATION_STOPPED: 'CANCELATION_STOPPED',
  GAME_REGBET_DISABLED: 'BAD_DRAW',
  GAME_CANCEL_TIME_EXCEEDED: 'CANCELATION_TIME_EXCEEDED',
  GAME_CANCELATION_RETAILER_NOT_POSSIBLE: 'CANCELATION_RETAILER_NOT_POSSIBLE',
  GAME_CANCEL_LIVESPEL: 'BAD_DRAW',
  GAME_UNABLE_TO_START_WINPAYOUT: 'BAD_RESULT',
  GAME_MAX_ROW_WINAMOUNT_EXCEEDED: 'BAD_RESULT',
  GAME_FUND_IS_BUSY: 'BAD_RESULT',
  GAME_BAD_RESULT_COMBINATION: 'BAD_RESULT',
  GAME_CANCEL_SUBSCRIPTION_BET: 'GENERIC',
  GAME_WINGEN_DISCREPANCY: 'BACKEND_ERROR',
  GAME_PRODUCT_CLOSED: 'BAD_DRAW',
  GAME_EVENT_REPLACED: 'BAD_DRAW',
  GAME_LIMIT_ALREADY_DEFINED: 'BACKEND_ERROR',
  GAME_BAD_NUMEVENTS_IN_BET: 'BAD_BET',
  GAME_REGBET_IN_PROGRESS: 'BAD_BET',
  GAME_UNBETTABLE_ODDS: 'BAD_BET',
  GAME_UNBETTABLE_SCORECAST_COMB: 'BAD_BET',
  GAME_OUTCOME_CANCELLED: 'BAD_DRAW',
  GAME_BAD_LIVEBET_COMB: 'BAD_BET',
  GAME_LIVEBET_TOO_EARLY: 'BAD_BET',
  GAME_BAD_LIVEBET_CONTEXT: 'BAD_BET',
  GAME_LIVEBET_TIMEOUT: 'BACKEND_ERROR',
  GAME_CANCEL_NOT_ALLOWED: 'BAD_DRAW',
  GAME_FRAC_DISCREPANCY: 'BACKEND_ERROR',
  GAME_REQUEST_TOO_EARLY: 'BAD_DRAW',
  GAME_CUST_MAX_DRAW_REGAMT: 'AMOUNT_LIMIT',
  GAME_CUST_MAX_DRAW_EGNARADER_REGAMT: 'AMOUNT_LIMIT',
  GAME_CUST_MAX_DAILY_PRODUCT_REGAMT: 'DAILY_AMOUNT_LIMIT',
  GAME_SYNDICATE_MAX_DRAW_REGAMT: 'AMOUNT_LIMIT',
  GAME_SYNDICATE_MAX_DRAW_EGNARADER_REGAMT: 'AMOUNT_LIMIT',
  GAME_SYNDICATE_MAX_DAILY_PRODUCT_REGAMT: 'DAILY_AMOUNT_LIMIT',
  GAME_RETAILER_MAX_DRAW_REGAMT: 'AMOUNT_LIMIT',
  GAME_RETAILER_MAX_DRAW_ANDELSSPEL_REGAMT: 'AMOUNT_LIMIT',
  GAME_RETAILER_MAX_DAILY_PRODUCT_REGAMT: 'DAILY_AMOUNT_LIMIT',
  GAME_ODDS_TOO_LOW: 'BAD_BET',
  GAME_ODDS_TOO_HIGH: 'BAD_BET',
  GAME_BET_AMOUNT_TOO_LOW: 'BAD_BET',
  GAME_BET_AMOUNT_TOO_HIGH: 'BAD_BET',
  GAME_CUST_MAX_DAILY_LIVE_REGAMT: 'CUSTOMER_LIMIT',
  GAME_BAD_DEFSRC: 'BAD_DRAW',
  GAME_UNSUPPORTED_EVENTDEF: 'BAD_DRAW',
  GAME_LIVE_DRAW_CONFLICT: 'BAD_DRAW',
  GAME_CUST_MAX_BOARDS_IN_DRAW: 'ROW_LIMIT',
  GAME_SYNDICATE_MAX_BOARDS_IN_DRAW: 'ROW_LIMIT',
  GAME_TOO_MANY_FAILED_EVENTS: 'BAD_DRAW',
  GAME_TOO_MANY_EVENTS_IN_DRAW: 'BAD_DRAW',
  GAME_DRAW_CLOSED_BY_OWN: 'BAD_DRAW',
  GAME_DRAW_CLOSED_BY_ODDSIMP: 'BAD_DRAW',
  GAME_UNABLE_TO_ARCHIVE: 'BACKEND_ERROR',
  GAME_BETAMT_INCLUDES_IMPROWS: 'BAD_BET',
  GAME_BET_CONTAINS_ONLY_IMPROWS: 'BAD_BET',
  GAME_BET_AMOUNT_TOO_LOW_AFTER_IMPROW_RED: 'BAD_BET',
  GAME_BAD_AUDITSTATE: 'BACKEND_ERROR',
  GAME_BAD_AUDITSERIAL: 'BACKEND_ERROR',
  GAME_CUSTSUP_MAX_SINGLE_BETS_EXCEEDED: 'CUSTOMER_LIMIT',
  GAME_BAD_RANKING_LIST: 'BACKEND_ERROR',
  GAME_FAILED_EXT_REGBET_REQUEST: 'BACKEND_ERROR',
  GAME_FAILED_EXT_CANCELBET_REQUEST: 'BACKEND_ERROR',
  GAME_TPPROXY_TIMEOUT: 'BACKEND_ERROR',
  GAME_BAD_RACE_STATE: 'BAD_DRAW',
  GAME_BAD_BET_STATE: 'BAD_BET',
  GAME_BET_ALREADY_COMPLETE: 'BAD_BET',
  GAME_BAD_REFUND_AMOUNT: 'BACKEND_ERROR',
  GAME_OPENHOURS_CONFLICT_WITH_NEXT_DRAW: 'BAD_DRAW',
  GAME_OPENHOURS_CONFLICT_WITH_PREV_DRAW: 'BAD_DRAW',
  GAME_BAD_WINCOUNT_TIME: 'BAD_DRAW',
  GAME_BAD_START_DATE: 'BAD_DRAW',
  GAME_BAD_STOP_DATE: 'BAD_DRAW',
  GAME_DATE_CONFLICT_WITH_OTHER_DRAW: 'BAD_DRAW',
  GAME_INCORRECT_NUM_PART_DRAWS: 'BAD_DRAW',
  GAME_BAD_DRAWTYPE: 'BAD_DRAW',
  GAME_BAD_PARTICIPATION_TERMS: 'BAD_DRAW',
  GAME_BAD_WINTYPE: 'BAD_RESULT',
  GAME_INCORRECT_DIV_WINNERS: 'BAD_RESULT',
  GAME_INCORRECT_TOT_WINNERS: 'BAD_RESULT',
  GAME_DATE_CONFLICT_BETWEEN_RCV_INTERVALS: 'BAD_DRAW',
  GAME_INCORRECT_TOT_WINAMOUNT: 'BAD_RESULT',
  GAME_WINCOUNT_TOO_EARLY: 'BAD_RESULT',
  GAME_SPELADMIN_REQUEST_MAXTIME: 'BACKEND_ERROR',
  COMP_INCORRECT_PARAMETER: 'COMPETITION_ERROR',
  COMP_COMPETITION_NOT_FOUND: 'COMPETITION_ERROR',
  COMP_ACTIVE_COMPETITION_NOT_OK: 'COMPETITION_ERROR',
  COMP_SIGNUP_TO_EARLY: 'COMPETITION_ERROR',
  COMP_ACTIVE_BET_NOT_OK: 'COMPETITION_ERROR',
  PACC_NOT_USED: 'GENERIC',
  PACC_ERROR: 'GENERIC',
  PACC_INVALID_MSG_ID: 'GENERIC',
  PACC_INCORRECT_PARAMETER: 'GENERIC',
  PACC_TIMEOUT: 'GENERIC',
  PACC_MODULE_NOT_AVAILABLE: 'BACKEND_ERROR',
  PACC_FILE_ERROR: 'GENERIC',
  PACC_NO_DATA_FOUND: 'GENERIC',
  PACC_INVALID_MSG_VERSION: 'GENERIC',
  PACC_SIZE_ERROR: 'GENERIC',
  PACC_NO_IPC_BUFFERS: 'GENERIC',
  PACC_NO_PRIVILEGE: 'GENERIC',
  PACC_PROCESS_IS_BUSY: 'GENERIC',
  PACC_INVALID_PROTOCOL_VERSION: 'GENERIC',
  PACC_NATIONAL_BLOCKED: 'USER_HAS_EXCLUSION',
  PACC_CREATE_RESERVE_ID: 'BACKEND_ERROR',
  PACC_UPDATE_RESERVE_ID: 'BACKEND_ERROR',
  PACC_HTTP_ERROR: 'BACKEND_ERROR',
  PACC_PARSE_RESPONSE: 'BACKEND_ERROR',
  PACC_TECHNICAL_ERROR_SPELPAUS: 'USER_HAS_EXCLUSION',
  PACC_NOT_ENOUGH_MONEY: 'NOT_ENOUGH_MONEY',
  PACC_USER_NOT_FOUND: 'BACKEND_ERROR',
  PACC_USER_BLOCKED: 'GENERIC',
  PACC_LIMIT_REACHED: 'TIME_LIMIT',
  PACC_WRONG_TARGET_USER: 'BACKEND_ERROR',
  PACC_WRONG_RESERVE_ID: 'BACKEND_ERROR',
  PACC_WRONG_PAM_AMOUNT: 'BACKEND_ERROR',
  MARKETPLACE_WAGER_ALREADY_PAID: 'MARKETPLACE_WAGER_ALREADY_PAID',
} as const;

const ITS_TABLE: Record<ITSFacility, Readonly<Record<string, ITS_ERROR>>> = {
  NOT_USED: ITS_NOT_USED,
  NOT_USED_2: ITS_NOT_USED2,
  AGENT: ITS_AGENT,
  GATEWAY: ITS_GATEWAY,
  RNGW: ITS_RNGW,
  TM: ITS_TM,
  ASM: ITS_ASM,
  RET: ITS_RET,
  GAME: ITS_GAME,
  SCORE: ITS_SCORE,
  MCAST: ITS_MCAST,
  CAMP: ITS_CAMP,
  VAL: ITS_VAL,
  LOTT: ITS_LOTT,
  SM: ITS_SM,
  LM: ITS_LM,
  TSM: ITS_TSM,
  DCACHE: ITS_DCACHE,
  WIN: ITS_WIN,
  TSTORE: ITS_TSTORE,
  MBVAL: ITS_MBVAL,
  OMS: ITS_OMS,
  SYN: ITS_SYN,
  BRC: ITS_BRC,
  SUB: ITS_SUB,
  PAY: ITS_PAY,
  VOM: ITS_VOM,
  COMP: ITS_COMP,
  PACC: ITS_PACC,
  TACC: ITS_TACC,
  RES: ITS_RES,
  INVALID_Facility: ITS_INVALID_FACILITY,
  NONE: ITS_NONE,
} as const;

function parseITSFacility(value: unknown): ITSFacility | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  if (!(value in ITS_FACILITY)) {
    return undefined;
  }

  return ITS_FACILITY[Number.parseInt(value, 10) as ITSFacilityNumeric];
}

function parseClientITSError(facility: ITSFacility, code: string): [ClientErrorCode, ITS_ERROR] | undefined {
  const itsError = ITS_TABLE[facility];

  if (code in itsError) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const error = itsError[code]!;
    return [ITS_ERROR[error], error] as const;
  }

  return undefined;
}

const getErrorCodeValue = (errorCode: string | number): string[] => {
  if (Number.isInteger(errorCode)) {
    return ['99', errorCode.toString()];
  }
  return errorCode.toString().split(' ');
};

const logITSError = (facilityCode?: string, code?: string): void => {
  logger.warn('Unhandled ITS Error', { facilityCode, code });
};

type ITSCode = string & { __type: 'ITSCode' };

function isItsCode(value: unknown): value is ITSCode {
  if (typeof value !== 'string') {
    return false;
  }

  return /^\d+$/.test(value);
}

/**
 * Function that parses ITS error code.
 *
 * @param errorCode - ITS error code.
 * @returns Array with ClientErrorCode, ITSFacility, ITSCode and ITS_ERROR.
 */
export function parseITSError(errorCode?: string): Readonly<[ClientErrorCode?, ITSFacility?, ITSCode?, ITS_ERROR?]> {
  let parsedITSCode;

  const [facilityCode, itsCode] = getErrorCodeValue(errorCode ?? '');

  if (isItsCode(itsCode)) {
    parsedITSCode = itsCode;
  }

  const itsFacility = parseITSFacility(facilityCode);

  if (!itsFacility || !parsedITSCode) {
    logITSError(facilityCode, itsCode);
    return [undefined, itsFacility, parsedITSCode];
  }

  const itsError = parseClientITSError(itsFacility, parsedITSCode);

  if (!itsError) {
    logITSError(facilityCode, itsCode);
    return [undefined, itsFacility, parsedITSCode];
  }

  const [clientError, itsClientError] = itsError;

  return [clientError, itsFacility, parsedITSCode, itsClientError] as const;
}
